import { PremiumBackground } from '../effects/PremiumBackground';
import React, { useState } from 'react';
import { 
Briefcase, 
  ArrowUpRight, 
  TrendingUp, 
  ShieldAlert, 
  ShieldCheck, 
  ArrowDown, 
  CheckCircle2, 
  Building2,
  Scale,
  Activity,
  Layers,
  Award,
  ChevronRight,
  FileCheck2
} from 'lucide-react';

export interface MarketPrecedentsProps {
  id?: string;
}

export const MarketPrecedents: React.FC<MarketPrecedentsProps> = ({ id = 'precedents' }) => {
  const [activeProject, setActiveProject] = useState<number>(0);

  const precedents = [
    {
      code: 'PROJECT ECLIPSE',
      tag: 'ПРЕЦЕДЕНТ 01 // СТАЦИОНАР & ХИРУРГИЯ',
      profile: 'Многопрофильный хирургический центр со стационаром (Москва, ЗАО)',
      ebitda: '42 млн ₽ / год',
      chairsBeds: '2 операционные, 14 коек, диагностика',
      initialOffer: '168 млн ₽ (4.0x EBITDA)',
      initialMultiple: '4.0x',
      initialRisk:
        'Покупатель давил на риск дробления через 2 ИП ведущих хирургов (ст. 54.1 НК РФ) и требовал дисконт 60 млн ₽ с удержанием 40% на 3 года.',
      attacker: 'Инвестиционный комитет Топ-3 федеральной медицинской сети',
      spIntervention:
        'Проведена налоговая санация: перевод врачей на прямые трудовые контракты с прозрачным KPI от маржи, оформление опциона начмеду, регистрация долгосрочной аренды на 8 лет в Росреестре. Устранена личная ответственность основателя.',
      finalClose: '285 млн ₽ (6.8x EBITDA)',
      finalMultiple: '6.8x EBITDA',
      delta: '+117 млн ₽',
      deltaPercent: '+69.6%',
      buyerType: 'Топ-3 Федеральная медицинская сеть',
      closingFormat: 'Share Deal 100% с эскроу-счетом'
    },
    {
      code: 'PROJECT NEXUS',
      tag: 'ПРЕЦЕДЕНТ 02 // ПРЕМИАЛЬНАЯ СТОМАТОЛОГИЯ',
      profile: 'Премиальная стоматологическая клиника на 8 кресел с КТ (Москва, ЦАО)',
      ebitda: '31 млн ₽ / год',
      chairsBeds: '8 кресел, КЛКТ, зуботехническая лаборатория',
      initialOffer: '135 млн ₽ (4.3x EBITDA)',
      initialMultiple: '4.3x',
      initialRisk:
        'Отказ институционалов от покупки из-за риска перепланировки рентген-кабинета без СЭЗ, спорного договора аренды и неоформленных авторских прав на медицинскую базу пациентов.',
      attacker: 'M&A-департамент профильного стоматологического холдинга',
      spIntervention:
        'Актуализировали СЭЗ и лицензию через ускоренный аудит, зарегистрировали исключительные права на МИС, устранили пункт о смене контроля (Change of Control) в аренде и вывели актив на закрытый аукцион между 2 холдингами.',
      finalClose: '217 млн ₽ (7.0x EBITDA)',
      finalMultiple: '7.0x EBITDA',
      delta: '+82 млн ₽',
      deltaPercent: '+60.7%',
      buyerType: 'Стоматологический инвестиционный холдинг',
      closingFormat: 'Share Deal 100% с аукционным пулом'
    },
    {
      code: 'PROJECT VANGUARD',
      tag: 'ПРЕЦЕДЕНТ 03 // РЕПРОДУКТИВНАЯ МЕДИЦИНА',
      profile: 'Центр репродуктивной медицины и женского здоровья (Санкт-Петербург)',
      ebitda: '55 млн ₽ / год',
      chairsBeds: 'ЭКО-лаборатория, амбулаторная хирургия, криобанк',
      initialOffer: '230 млн ₽ (4.1x EBITDA)',
      initialMultiple: '4.1x',
      initialRisk:
        'Основатель — главный репродуктолог с персональным брендом. Инвесторы опасались обвала выручки на 50% в случае прекращения его практики (Key-Man Risk) и требовали рассрочку на 5 лет.',
      attacker: 'Private Equity фонд прямых инвестиций в здравоохранение',
      spIntervention:
        'Сконструирована гибридная модель Rollover Equity: 80% гарантированный Cash-out основателю в день сделки, 20% конвертированы в акции материнского холдинга с 2-летним опционом и контрактом в статусе научного руководителя.',
      finalClose: '396 млн ₽ (7.2x EBITDA)',
      finalMultiple: '7.2x EBITDA',
      delta: '+166 млн ₽',
      deltaPercent: '+72.1%',
      buyerType: 'Private Equity фонд в сфере здравоохранения',
      closingFormat: 'Rollover Equity (80% Cash / 20% Equity)'
    },
  ];

  const current = precedents[activeProject];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Прецеденты и закрытые мандаты Structure & Partners",
    "description": "Примеры защиты стоимости медицинских активов: от отражения дисконтов аудиторов до закрытия сделок по мультипликаторам до 7.2x EBITDA.",
    "itemListElement": precedents.map((p, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": `${p.code}: ${p.profile}`,
      "description": `Прирост стоимости: ${p.delta}. Финальная оценка: ${p.finalClose}.`
    }))
  };

  return (
    <section
      id={id}
      aria-label="Анатомия рыночных прецедентов: закрытые мандаты S&P"
      className="relative w-full bg-[#FAF8F5] text-[#141416] py-24 md:py-32 border-t border-[#EAE5DE] font-sans overflow-hidden"
    >
      {/* ТАКТИЛЬНЫЙ МАКРО-РЕЛЬЕФ */}
      <PremiumBackground variant={8} />

      
      

      {/* JSON-LD Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      {/* Delicate background ambient tones */}
      <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(140,109,55,0.04)_0%,transparent_70%)] pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(30,43,69,0.02)_0%,transparent_70%)] pointer-events-none" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        
        {/* =========================================================================
            1. НАДЗАГОЛОВОК, 2. ЗАГОЛОВОК H2, 3. ПОДЗАГОЛОВОК-МАНИФЕСТ
            ========================================================================= */}
        <div className="max-w-4xl mb-16 flex flex-col gap-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-[10px] sm:text-[11px] text-[#8C6D37] uppercase font-bold tracking-[0.2em] bg-[#8C6D37]/10 border border-[#8C6D37]/30 px-3 py-1 rounded-[2px] flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37]" />
              ЭТАЖ 13 // РЕЕСТР ПРЕЦЕДЕНТОВ // M&A-ДОКАЗАТЕЛЬСТВА
            </span>
            <span className="font-mono text-[10px] text-[#64748B] uppercase tracking-[0.15em] bg-[#F7F4EE] border border-[#EAE5DE] px-2.5 py-1 rounded-[2px] font-medium">
              VERIFIED MANDATES
            </span>
          </div>

          <h2 className="text-3xl sm:text-5xl lg:text-[54px] font-serif font-semibold text-[#141416] tracking-[-0.02em] leading-[1.08]">
            Анатомия рыночных прецедентов: <br />
            <span className="text-[#64748B] italic font-serif">
              как защита стоимости принесла от 82 до 166 млн ₽ чистой дельты
            </span>
          </h2>

          <div className="text-[15px] sm:text-[16px] text-[#334155] leading-[1.75] font-sans font-normal space-y-3 max-w-3xl pl-5 border-l-[4px] border-[#8C6D37] bg-[#F7F4EE]/60 py-3 rounded-r-[2px] border-y border-r border-[#EAE5DE]">
            <p>
              Реальные разобранные кейсы защиты стоимости медицинских активов. 
            </p>
            <p className="text-[#141416] font-medium">
              Мы строго соблюдаем бессрочное соглашение о неразглашении персональных данных клиентов, но полностью раскрываем финансовую и юридическую механику побед.
            </p>
          </div>
        </div>

        {/* =========================================================================
            3 ПРЕЦЕДЕНТНЫЕ КАРТОЧКИ СЕЛЕКТОРА
            ========================================================================= */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 relative">
          {precedents.map((p, idx) => {
            const isSelected = activeProject === idx;
            return (
              <div
                key={p.code}
                onClick={() => setActiveProject(idx)}
                className={`group relative rounded-[2px] p-6 sm:p-8 cursor-pointer transition-all duration-300 flex flex-col justify-between border overflow-hidden ${
                  isSelected
                    ? 'border-[#8C6D37] bg-white shadow-sm ring-1 ring-[#8C6D37] z-10'
                    : 'border-[#EAE5DE] hover:border-[#CBD5E1] bg-white shadow-xs'
                }`}
              >
                {/* Active marker edge */}
                <div className={`absolute top-0 left-0 w-[3px] h-full transition-all duration-300 ${
                  isSelected ? 'bg-[#8C6D37]' : 'bg-transparent group-hover:bg-[#8C6D37]/30'
                }`} />

                <div className="relative z-10 pl-2">
                  <div className="flex items-center justify-between gap-2 mb-5">
                    <span className={`font-mono text-[10px] uppercase font-bold tracking-[0.2em] transition-colors duration-300 ${isSelected ? 'text-[#8C6D37]' : 'text-[#64748B] group-hover:text-[#8C6D37]'}`}>
                      {p.code}
                    </span>
                    <span className={`font-mono text-[11px] font-bold tracking-wider px-3 py-1 rounded-[2px] border transition-all duration-300 ${
                      isSelected 
                        ? 'text-[#8C6D37] bg-[#8C6D37]/10 border-[#8C6D37]/40 shadow-xs' 
                        : 'text-[#141416] bg-[#F7F4EE] border-[#EAE5DE] group-hover:border-[#8C6D37]/30'
                    }`}>
                      Δ {p.delta}
                    </span>
                  </div>

                  <h3 className={`text-[16px] sm:text-[17px] font-serif font-semibold leading-[1.45] transition-colors duration-300 ${
                    isSelected ? 'text-[#141416]' : 'text-[#334155] group-hover:text-[#141416]'
                  }`}>
                    {p.profile}
                  </h3>
                </div>

                <div className={`relative z-10 pt-5 mt-6 flex items-center justify-between font-mono text-[11px] transition-all duration-300 pl-2 ${
                  isSelected ? 'border-t border-[#8C6D37]/30 text-[#334155]' : 'border-t border-[#EAE5DE] text-[#64748B]'
                }`}>
                  <span className="uppercase tracking-[0.15em]">EBITDA: {p.ebitda}</span>
                  <span className={`font-bold tracking-wider ${isSelected ? 'text-[#8C6D37]' : 'text-[#141416]'}`}>{p.finalMultiple}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* =========================================================================
            ГЛУБОКИЙ РАЗБОР АКТИВНОГО ПРЕЦЕДЕНТА (DOSSIER VIEW)
            ========================================================================= */}
        <div className="relative rounded-[2px] border border-[#8C6D37]/40 bg-white p-8 sm:p-12 md:p-14 shadow-xs mb-16 overflow-hidden">
          {/* Header Досье */}
          <div className="relative z-10 flex flex-col xl:flex-row xl:items-start justify-between gap-8 pb-10 border-b border-[#EAE5DE]">
            <div className="max-w-3xl">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="font-mono text-[10px] text-[#8C6D37] font-bold uppercase tracking-[0.2em] bg-[#8C6D37]/10 px-3.5 py-1.5 rounded-[2px] border border-[#8C6D37]/30">
                  РЕЗУЛЬТАТ МАНДАТА S&P // {current.code}
                </span>
                <span className="font-mono text-[10px] text-[#64748B] uppercase tracking-[0.15em] bg-[#F7F4EE] px-2.5 py-1.5 rounded-[2px] border border-[#EAE5DE]">
                  {current.chairsBeds}
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl lg:text-[36px] font-serif font-semibold text-[#141416] tracking-tight leading-[1.15]">
                {current.profile}
              </h3>
            </div>

            {/* Метрика Дельты */}
            <div className="flex items-center gap-6 sm:gap-8 bg-[#F7F4EE] border border-[#EAE5DE] p-6 sm:p-7 rounded-[2px] shadow-xs shrink-0 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-[3px] h-full bg-[#8C6D37]" />
              <div>
                <span className="font-mono text-[10px] text-[#64748B] uppercase block tracking-[0.15em] mb-1 font-bold">Годовая EBITDA</span>
                <span className="font-serif text-xl sm:text-2xl font-bold text-[#141416]">{current.ebitda}</span>
              </div>
              <div className="h-10 w-px bg-[#CBD5E1]" />
              <div>
                <span className="font-mono text-[10px] text-[#8C6D37] uppercase block font-bold tracking-[0.15em] mb-1">Чистая премия</span>
                <span className="font-serif text-2xl sm:text-3xl font-bold text-[#8C6D37]">{current.delta}</span>
              </div>
            </div>
          </div>

          {/* 2 Колонки: Атака покупателя vs Защита S&P */}
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 pt-10">
            
            {/* Левая колонка: Уязвимость и атака покупателя */}
            <div className="space-y-6">
              <div className="relative bg-[#FFF8F8] border border-[#E5C3C3] rounded-[2px] p-7 sm:p-8 shadow-xs overflow-hidden">
                <div className="absolute top-0 left-0 w-[3px] h-full bg-[#991B1B]" />
                
                <div className="flex items-center gap-3 mb-5">
                  <ShieldAlert className="w-5 h-5 text-[#991B1B]" />
                  <span className="font-mono text-[10px] sm:text-[11px] font-bold text-[#991B1B] uppercase tracking-[0.15em]">
                    АТАКА ПОКУПАТЕЛЯ И ПЕРВИЧНЫЙ ОФФЕР
                  </span>
                </div>

                <div className="font-mono text-[14px] sm:text-[15px] font-bold text-[#991B1B] mb-4 bg-white px-3.5 py-2.5 rounded-[2px] border border-[#E5C3C3] inline-block shadow-xs">
                  Первичный оффер: {current.initialOffer}
                </div>

                <p className="text-[14px] sm:text-[15px] text-[#334155] leading-[1.7] font-normal mb-6">
                  {current.initialRisk}
                </p>

                <div className="pt-4 border-t border-[#E5C3C3]/60 text-[11px] font-mono text-[#64748B] flex flex-col gap-1">
                  <span className="uppercase text-[#64748B] tracking-[0.15em] text-[10px] font-bold">Профиль покупателя:</span>
                  <span className="text-[#141416] font-semibold text-[13px]">{current.attacker}</span>
                </div>
              </div>

              {/* Покупатель актива */}
              <div className="bg-[#F7F4EE] border border-[#EAE5DE] rounded-[2px] p-6 shadow-xs">
                <span className="font-mono text-[10px] uppercase font-bold text-[#64748B] block mb-2 tracking-[0.2em]">
                  ИНСТИТУЦИОНАЛЬНЫЙ ПОКУПАТЕЛЬ АКТИВА
                </span>
                <div className="text-[16px] font-serif font-semibold text-[#141416] mb-2">
                  {current.buyerType}
                </div>
                <div className="text-[12px] text-[#64748B] font-mono tracking-wider pt-2.5 border-t border-[#EAE5DE]">
                  <span className="text-[#64748B]">ФОРМАТ ЗАКРЫТИЯ:</span> <strong className="text-[#141416] font-semibold ml-1">{current.closingFormat}</strong>
                </div>
              </div>
            </div>

            {/* Правая колонка: Институциональная защита S&P и Итог */}
            <div className="space-y-6">
              <div className="relative bg-white border border-[#8C6D37]/40 rounded-[2px] p-7 sm:p-8 shadow-xs overflow-hidden">
                <div className="absolute top-0 left-0 w-[3px] h-full bg-[#8C6D37]" />
                
                <div className="flex items-center gap-3 mb-5">
                  <ShieldCheck className="w-5 h-5 text-[#8C6D37]" />
                  <span className="font-mono text-[10px] sm:text-[11px] font-bold text-[#8C6D37] uppercase tracking-[0.15em]">
                    ИНСТИТУЦИОНАЛЬНАЯ ЗАЩИТА S&P
                  </span>
                </div>

                <p className="text-[14px] sm:text-[15px] text-[#334155] leading-[1.7] font-normal mb-6">
                  {current.spIntervention}
                </p>

                <div className="pt-4 border-t border-[#EAE5DE] flex items-center gap-2.5 text-[11px] font-mono text-[#141416] tracking-[0.15em] bg-[#F7F4EE] p-3 rounded-[2px] border border-[#8C6D37]/30">
                  <CheckCircle2 className="w-4 h-4 text-[#8C6D37] shrink-0" />
                  <span className="font-bold">ПОЛНАЯ ЮРИДИЧЕСКАЯ ИЗОЛЯЦИЯ ОТ СУБСИДИАРНЫХ ПРЕТЕНЗИЙ</span>
                </div>
              </div>

              {/* Итоговая сумма сделки - Архитектурный контрастный блок */}
              <div className="relative overflow-hidden bg-[#141416] text-white border border-[#8C6D37]/40 rounded-[2px] p-7 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6 shadow-xs">
                <div className="relative z-10">
                  <span className="font-mono text-[10px] uppercase font-bold text-[#8C6D37] block mb-2 tracking-[0.2em] flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37]" />
                    ИТОГОВАЯ СУММА ЗАКРЫТИЯ
                  </span>
                  <div className="font-serif text-3xl sm:text-4xl font-bold text-white tracking-tight">
                    {current.finalClose}
                  </div>
                </div>
                
                <div className="relative z-10 sm:text-right shrink-0">
                  <span className="font-mono text-[10px] uppercase font-bold text-[#94A3B8] block mb-1.5 tracking-[0.15em]">
                    Итоговый мультипликатор
                  </span>
                  <span className="font-mono text-xl sm:text-2xl text-[#8C6D37] font-bold">
                    {current.finalMultiple}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* =========================================================================
            CALLOUT BOX: ЗАКОН СОХРАНЕНИЯ КАПИТАЛА
            ========================================================================= */}
        <div className="relative rounded-[2px] border border-[#8C6D37]/40 p-8 sm:p-10 mb-16 shadow-xs bg-white overflow-hidden">
          <div className="absolute top-0 left-0 w-[4px] h-full bg-[#8C6D37]" />

          <div className="flex flex-col md:flex-row items-start gap-6 relative z-10">
            <div className="w-12 h-12 rounded-[2px] bg-[#F7F4EE] border border-[#8C6D37]/30 flex items-center justify-center shrink-0 shadow-xs text-[#8C6D37]">
              <Scale className="w-6 h-6" />
            </div>
            <div className="space-y-4 max-w-4xl">
              <h4 className="text-xl sm:text-[24px] font-serif font-semibold text-[#141416] tracking-tight leading-snug">
                Закон сохранения капитала в медицинском M&A
              </h4>
              <p className="text-[15px] sm:text-[16px] text-[#334155] leading-[1.7] font-normal">
                Покупатель никогда не отступает перед эмоциональными аргументами вроде «я строил эту клинику 15 лет».
                Покупатель отступает только перед встречным аудиторским заключением, подтвержденным судебной и налоговой практикой.
              </p>
              <div className="bg-[#F7F4EE] p-4 sm:p-5 rounded-[2px] border-l-2 border-[#8C6D37] border-y border-r border-[#EAE5DE]">
                <p className="text-[15px] text-[#141416] font-semibold leading-[1.6]">
                  Каждый вложенный миллион в подготовку актива с Structure & Partners возвращает собственнику <span className="text-[#8C6D37] font-serif italic text-lg px-1">от 10 до 35 миллионов рублей</span> защищенной капитализации на закрытии сделки.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* =========================================================================
            НАВИГАТОР ПЕРЕХОДА К СЛЕДУЮЩЕМУ БЛОКУ (ЭТАЖ 14: KNOWLEDGE VAULT)
            ========================================================================= */}
        <div className="text-center pt-2">
          <a
            href="#vault-docs"
            className="inline-flex items-center gap-2 font-mono text-[11px] sm:text-[12px] text-[#64748B] hover:text-[#141416] transition-all py-3 px-6 rounded-[2px] bg-white hover:bg-[#F7F4EE] border border-[#EAE5DE] hover:border-[#8C6D37]/40 shadow-xs group tracking-wider uppercase font-bold"
          >
            <span>[ ПЕРЕЙТИ К ЭТАЖУ 14: ЗАКРЫТЫЕ АНАЛИТИЧЕСКИЕ МЕМОРАНДУМЫ S&P ↓ ]</span>
            <ArrowDown className="w-3.5 h-3.5 text-[#8C6D37] group-hover:translate-y-0.5 transition-transform" />
          </a>
        </div>

      </div>
    </section>
  );
};
